import React, { useEffect, useRef, useState } from "react";
import styles from "./Loader.module.css";
import { subscribeLoading } from "./loadingTracker";

// Only show for waits the user can notice - a fast request finishing inside
// this window never flashes the bar at all.
const SHOW_DELAY_MS = 180;
const TRICKLE_MS = 250;

/**
 * Slim branded progress bar pinned to the very top of the window, driven by
 * loadingTracker (API requests + page loads). It creeps toward 90% while
 * work is in flight - fast at first, slower as it goes, since the real
 * duration is unknown - then snaps to 100% and fades out when everything
 * has finished.
 */
export default function TopProgressBar() {
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const timers = useRef({});

  useEffect(() => {
    const clearTimers = () => {
      clearTimeout(timers.current.show);
      clearTimeout(timers.current.hide);
      clearInterval(timers.current.trickle);
      timers.current = {};
    };

    const start = () => {
      if (timers.current.show || timers.current.trickle) return;
      clearTimeout(timers.current.hide);
      timers.current.show = setTimeout(() => {
        timers.current.show = null;
        setVisible(true);
        setProgress(8);
        timers.current.trickle = setInterval(() => {
          setProgress((p) => (p >= 90 ? p : p + (90 - p) * 0.12));
        }, TRICKLE_MS);
      }, SHOW_DELAY_MS);
    };

    const finish = () => {
      const wasShowing = !!timers.current.trickle;
      clearTimers();
      if (!wasShowing) {
        setVisible(false);
        setProgress(0);
        return;
      }
      setProgress(100);
      timers.current.hide = setTimeout(() => {
        setVisible(false);
        timers.current.hide = setTimeout(() => setProgress(0), 250);
      }, 300);
    };

    const unsubscribe = subscribeLoading((active) => (active > 0 ? start() : finish()));
    return () => {
      unsubscribe();
      clearTimers();
    };
  }, []);

  return (
    <div
      className={`${styles.topBar} ${visible ? styles.topBarVisible : ""}`}
      role="progressbar"
      aria-hidden={!visible}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(progress)}
    >
      <div className={styles.topBarFill} style={{ transform: `scaleX(${progress / 100})` }}>
        <span className={styles.topBarGlow} />
      </div>
    </div>
  );
}
